import { getKpisBalance } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, formatPeriodLabel } from "./utils/dateRange";

// Módulo del tab KPIs de Balance. Mismo contrato que kpisReport: el tab lee
// los filtros COMPARTIDOS (`reportsFilters` + `financialReports.filterPeriod`)
// y se los pasa explícitos a la action. Fases 1-3 (docs/DECISIONES.md §8):
// liquidez responde capital de trabajo, caja, cuentas (saldos + días FIFO) y
// razones; solvencia sus ratios, deuda financiera y ROA/ROE. Solo Solvencia
// G3 (carga financiera) sigue placeholder (fase 3).

function normalizeLiquidez(data, monthHeaders) {
    const charts = data.charts || {};
    const capital = charts.capital_trabajo || {};
    const caja = charts.caja || {};
    const cuentas = charts.cuentas || {};
    const razones = charts.razones || {};
    return {
        group: "liquidez",
        monthHeaders,
        capitalTrabajo: {
            activoCirculante: capital.activo_circulante || [],
            pasivoCirculante: capital.pasivo_circulante || [],
            capitalTrabajo: capital.capital_trabajo || [],
        },
        caja: {
            caja: caja.caja || [],
            equivalentes: caja.equivalentes || [],
            cajaSobreActivosPct: caja.caja_sobre_activos_pct || [],
        },
        cuentas: {
            cuentasPorCobrar: cuentas.cuentas_por_cobrar || [],
            cuentasPorPagar: cuentas.cuentas_por_pagar || [],
            inventario: cuentas.inventario || [],
            diasCobrar: cuentas.dias_cobrar || [],
            diasPagar: cuentas.dias_pagar || [],
            diasInventario: cuentas.dias_inventario || [],
        },
        razones: {
            razonCorriente: razones.razon_corriente || [],
            razonAcida: razones.razon_acida || [],
            cicloConversion: razones.ciclo_conversion || [],
        },
    };
}

function normalizeSolvencia(data, monthHeaders) {
    const charts = data.charts || {};
    const ratios = charts.ratios || {};
    const rentabilidad = charts.rentabilidad || {};
    const deuda = charts.deuda_financiera || {};
    const carga = charts.carga_financiera || {};
    return {
        group: "solvencia",
        monthHeaders,
        ratios: {
            pasivosActivos: ratios.pasivos_activos || [],
            pasivosPatrimonio: ratios.pasivos_patrimonio || [],
            patrimonioActivos: ratios.patrimonio_activos || [],
        },
        cargaFinanciera: {
            interesesSobreVentasPct: carga.intereses_sobre_ventas_pct || [],
        },
        deudaFinanciera: {
            total: deuda.total || [],
            neta: deuda.neta || [],
            cpSobreTotalPct: deuda.cp_sobre_total_pct || [],
            lpSobreTotalPct: deuda.lp_sobre_total_pct || [],
        },
        rentabilidad: {
            roaPct: rentabilidad.roa_pct || [],
            roePct: rentabilidad.roe_pct || [],
        },
    };
}

export function normalizeKpisBalance(data) {
    const monthHeaders = (data.period_labels || []).map(
        (iso) => formatPeriodLabel(iso, data.period || "month"),
    );
    if (data.group === "solvencia") {
        return normalizeSolvencia(data, monthHeaders);
    }
    return normalizeLiquidez(data, monthHeaders);
}

const state = () => ({
    kpisData: null,
    isLoading: false,
    error: null,
});

const mutations = {
    SET_KPIS_DATA(state, payload) {
        state.kpisData = payload;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
};

const actions = {
    async fetchKpisBalance({ commit, rootState }, {
        endMonth,
        selectedPeriodRange,
        customRange = null,
        customYearRange = null,
        period = "month",
        group = "liquidez",
    } = {}) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { initDate, endDate } = resolveDateRange(
                endMonth,
                selectedPeriodRange,
                period,
                customRange,
                customYearRange,
                rootState.reportsFilters.dataFirstMonth,
            );
            const response = await getKpisBalance(initDate, endDate, group, period);
            commit("SET_KPIS_DATA", normalizeKpisBalance(response.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[kpisBalance]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar los KPIs."));
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
